import React, { useCallback, useMemo, useState } from "react";
import { StyleSheet, useWindowDimensions } from "react-native";

import { type ReservationWorkerFragment, callNumber, match, useToday } from "@stepapp/core";
import { ReservationStatus } from "@stepapp/core/apiTypes";
import { captureSentryException } from "@stepapp/sentry";
import {
  Avatar,
  Block,
  BottomSheet,
  Divider,
  IconButton,
  type IconNames,
  ProgressBar,
  SvgIcon,
  Touchable,
  Typography,
  palette,
  spacing,
} from "@stepapp/ui";
import { addDays, differenceInCalendarDays, format } from "date-fns";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";

type Props = {
  workers: (ReservationWorkerFragment | null)[];
  hideActions?: boolean;
  orderId?: string;
  status?: ReservationStatus;
  startDate?: string;
};

type WorkerStatus = {
  status: ReservationStatus;
  statusTitle: string;
  statusDescription: string;
  icon: IconNames;
};

const WorkerWithStatus = ({ workers, status, orderId, hideActions = false, startDate }: Props) => {
  const { t } = useTranslation();
  const today = useToday();
  const { navigate } = useRouter();
  const [statusModalVisible, setStatusModalVisible] = useState(false);

  const { width: screenWidth } = useWindowDimensions();

  const finalDayToLeaveReview = format(addDays(startDate || today, 6), "yyyy-MM-dd");
  const daysLeft = (differenceInCalendarDays(finalDayToLeaveReview, today) || 0) + 1;

  const serviceStatuses: WorkerStatus[] = useMemo(
    () => [
      {
        icon: "assigned",
        status: ReservationStatus.Assigned,
        statusTitle: t("screens.serviceRealization.statusChoosen"),
        statusDescription: t("screens.serviceRealization.statusChoosenDescription"),
      },
      {
        icon: "clean",
        status: ReservationStatus.WorkStarted,
        statusTitle: t("screens.serviceRealization.statusInProgress"),
        statusDescription: t("screens.serviceRealization.statusInProgressDescription"),
      },
      {
        icon: "done",
        status: ReservationStatus.CustomerReview,
        statusTitle: t("screens.serviceRealization.serviceCompleted"),
        statusDescription: "",
      },
    ],
    [t],
  );

  const reservationStatus: WorkerStatus | null = useMemo(
    () => serviceStatuses.find((service) => service.status === status) || null,
    [status, serviceStatuses],
  );

  const progressBarWidth = match(screenWidth)
    .when(
      (w) => w >= 620,
      () => spacing.unit * 65.5,
    )
    .when(
      (w) => w >= 380,
      (w) => w - 96,
    )
    .otherwise(() => 380 - 96);

  const activeIndex = useMemo(
    () => serviceStatuses.findIndex((service) => service.status === status),
    [status, serviceStatuses],
  );

  const navigateToWorkerProfile = useCallback(
    (workerId: string) => {
      if (orderId) {
        navigate(`/calendar/${orderId}/assigned-worker?workerId=${workerId}`);
      } else {
        captureSentryException("[WorkerWithStatus]: navigateToWorkerProfile: no order", {
          extra: {
            orderId,
          },
        });
      }
    },
    [navigate, orderId],
  );

  return (
    <>
      <Block justifySpace borderRadius={2} padding={1.5} style={styles.container}>
        {workers?.map((worker, i) => {
          const lastItem = workers?.length - 1 === i;

          return (
            <Block key={worker?.id} alignCenter justifySpace row marginBottom={lastItem ? 0 : 1}>
              <Touchable
                onPress={() => navigateToWorkerProfile(worker?.id || "")}
                style={styles.workerInfo}
              >
                <Avatar confirmed size={spacing.unit * 6.5} uri={worker?.avatar} />
                <Block marginLeft={1.675}>
                  <Typography type="secondary" variant="lightLabel">
                    {t("screens.serviceCard.worker")}
                  </Typography>
                  <Typography type="primary" variant="answerTitle">
                    {worker?.name}
                  </Typography>
                </Block>
              </Touchable>

              {worker?.phone && !hideActions && (
                <IconButton
                  onPress={() => callNumber(worker?.phone)}
                  size={spacing.unit * 6.5}
                  style={styles.phoneButton}
                  name="phone"
                />
              )}
            </Block>
          );
        })}

        {match(status)
          .with(ReservationStatus.Assigned, ReservationStatus.WorkStarted, () => (
            <>
              <Divider color={palette.secondary.grey} />
              <Block alignCenter justifySpace row>
                <Block alignCenter row height={2.5}>
                  <SvgIcon
                    color={palette.primary.pinkSalmon}
                    name={reservationStatus?.icon || "clean"}
                    size={22}
                  />
                  <Typography style={styles.statusInfo} type="secondary" variant="paragraph">
                    {reservationStatus?.statusTitle}
                  </Typography>
                </Block>

                <Touchable
                  onPress={() => setStatusModalVisible(true)}
                  style={styles.statusInfoIcon}
                >
                  <SvgIcon color={palette.font.secondary} name="info" size={22} />
                </Touchable>
              </Block>
            </>
          ))
          .with(ReservationStatus.CustomerReview, ReservationStatus.Completed, () => (
            <Block marginTop={1}>
              <Divider color={palette.secondary.grey} />
              <Block row marginBottom={1.5} marginTop={1} justifySpace>
                <Typography variant="lightLabel" numberOfLines={2} style={styles.rateServiceDays}>
                  {t("screens.serviceRealization.rateTheServiceWithin")}
                </Typography>
                <Typography variant="lightLabel" style={styles.rateServiceDaysLeft}>
                  {t("screens.serviceRealization.still")}{" "}
                  <Typography variant="label">
                    {daysLeft === 1
                      ? t("screens.serviceRealization.daysLeft", { daysLeft })
                      : t("screens.serviceRealization.daysLeft_plural", { daysLeft })}
                  </Typography>
                </Typography>
              </Block>
              <ProgressBar
                cap={7}
                value={8 - daysLeft}
                width={progressBarWidth}
                height={8}
                type="pink"
                gradient
              />
            </Block>
          ))
          .otherwise(() => null)}
      </Block>

      <BottomSheet
        contentStyle={styles.statusContainer}
        enableScroll={false}
        toggle={() => setStatusModalVisible(false)}
        visible={statusModalVisible}
      >
        <Block marginHorizontal={2.5} marginTop={2.5}>
          <Block row>
            <Touchable onPress={() => setStatusModalVisible(false)}>
              <Block alignCenter justifyCenter style={styles.closeIcon}>
                <SvgIcon color={palette.common.gray} name="cross" size={36} />
              </Block>
            </Touchable>
          </Block>

          <Block marginBottom={1.5} marginTop={2.5}>
            <Typography variant="newH2">{t("screens.serviceRealization.serviceStatus")}</Typography>
          </Block>

          <Divider color={palette.common.gray} style={styles.divider} />

          <Block marginBottom={5} marginTop={2}>
            {serviceStatuses.map((service, i) => (
              <Block key={service.status}>
                <Block alignCenter row marginVertical={0.5}>
                  <SvgIcon
                    color={activeIndex >= i ? palette.primary.pinkSalmon : palette.common.disabled}
                    name={service.icon}
                    size={24}
                  />
                  <Typography
                    style={styles.statusInfo}
                    type={activeIndex >= i ? "primary" : "disabled"}
                    variant={activeIndex >= i ? "bodyBold" : "paragraph"}
                  >
                    {service.statusTitle}
                  </Typography>
                </Block>

                <Block row>
                  {i !== serviceStatuses.length - 1 && (
                    <Divider
                      spaceless
                      vertical
                      color={activeIndex > i ? palette.primary.pinkSalmon : palette.common.disabled}
                      style={styles.dividerStatus}
                      width={2}
                    />
                  )}

                  <Block flex={1} marginBottom={2} marginTop={0.5}>
                    {status === service.status && (
                      <Typography
                        style={styles.statusDescription}
                        type="secondary"
                        variant="paragraph"
                      >
                        {service.statusDescription}
                      </Typography>
                    )}
                  </Block>
                </Block>
              </Block>
            ))}
          </Block>
        </Block>
      </BottomSheet>
    </>
  );
};

export default React.memo(WorkerWithStatus);

const styles = StyleSheet.create({
  phoneButton: {
    marginLeft: 14,
  },
  closeIcon: {
    backgroundColor: palette.common.black,
    borderRadius: 20,
    marginLeft: spacing.unit * 0.5,
    marginVertical: spacing.unit * 0.5,
  },
  workerInfo: {
    flexDirection: "row",
    alignItems: "center",
  },
  container: {
    backgroundColor: palette.common.grayBackground,
  },
  divider: {
    marginHorizontal: -spacing.unit * 2.5,
  },
  dividerStatus: {
    marginLeft: spacing.unit * 0.35,
  },
  statusContainer: {
    padding: 0,
  },
  statusDescription: {
    marginLeft: spacing.unit * 3.3,
  },
  statusInfo: {
    marginLeft: spacing.unit * 2,
  },
  statusInfoIcon: {
    paddingLeft: spacing.unit * 1,
    paddingRight: spacing.unit * 1.5,
    paddingVertical: spacing.unit * 1.2,
  },
  rateServiceDays: {
    flex: 1,
  },
  rateServiceDaysLeft: {
    marginLeft: spacing.unit * 2,
  },
});
